import type { FC } from 'react'
import {
	Box,
	ChakraProps,
	Flex,
	Image,
	Text,
	useDisclosure,
	Button,
} from '@chakra-ui/react'
import {
	SeoBanner as SeoBannerWrapper,
	SeoBannerAvatar,
	SeoBannerButton,
	SeoBannerFooter,
	SeoBannerMainSection,
	SeoBannerImageText,
	SeoBannerMainText,
} from '@alexanderathoodly/ui-library'
import { useTranslation, Trans } from 'react-i18next'
import EssveeImg from '#/logos/essve.png'
import HumbleGroupImg from '#/logos/humblegroup.png'
import JuniImg from '#/logos/juni.png'
import LissieImg from '#/logos/lassie.png'
import {
	getIsEnhancedSERPUrl,
	getEnhancedSERPOfficeType,
	getOfficeTypeTranslation,
} from './enhancementutils'
import type { EnhancedSERPContentWithExpertMap } from './enhancementutils'
import { SALES_TEAM, SalesPerson, getTeamMemberName } from '~/utils/teams'
import { useTo } from './banners'
import { Link } from 'react-router-dom'

const PAR = SALES_TEAM['Pär Lövgren']
const DAVID = SALES_TEAM['David Kostadinovic']
const VIKTOR = SALES_TEAM['Viktor Söderström Hahn']
const TIM = SALES_TEAM['Tim Hodell']

const BANNER_CONTENT: EnhancedSERPContentWithExpertMap = {
	stockholm: {
		office: {
			content: {
				heading: 'Mina bästa tips: Hitta ert drömkontor i Stockholm',
				text: `Stockholm, som länge varit en av Europas ledande kontorsmarknader, har genomgått en kraftig förändring de senaste 5 åren. Från ett läge där det knappt fanns kontor att hyra har vakanserna stigit till över 10% på Stockholmsmarknaden i stort. Idag finns det både en uppsjö av alternativ och en trend mot att hyresvärdar vidareutvecklar sina kontorsprodukter med ytterligare innehåll så som service och kortare bindningstider. Personligen tycker jag, som får vara med och hjälpa hyresgäster att välja rätt kontor dagligen, att möjligheten att jämföra och hitta rätt är större än någonsin. Här är mina bästa tips på hur ni kan tänka för en lyckad kontorssökning.
                <br/><br/>
                <strong>Vad kostar det att hyra ett kontor i Stockholm?</strong>
                <br/>
                Hyrorna i Stockholm varierar enormt beroende på område och typ av kontor. Typiskt är ca 2-5 000kr/person/månad utanför tullarna i områden så som Kista och Solna, 4-7 000kr utanför city exempelvis Södermalm, och 7-12 000kr inne runt Sergels Torg och Stureplan. Kan ni tänka er att skippa att sitta just på Kungsgatan / Stureplan finns det pengar att spara. Min erfarenhet är att närheten till Stockholms tunnelbana är det som värderas högst av de anställda och bara genom att välja ett kontor några hundra meter bort kan kostnaden minska avsevärt.
                <br/><br/>
                <strong>Vad utmärker Stockholmsmarknaden för dig?</strong>
                <br/>
                I min roll jobbar jag mestadels med företag som söker kontor med mellan 20-50 arbetsplatser, och det är ett segment som verkligen kryllar av bra alternativ just i Stockholm. Tack vare att det öppnat över 200 000 kvm kontorshotell bara de senaste åren i Stockholm så finns det många kontor i den storleken som är utformade för större team från start. Det senaste året har även utbudet av egna kontor men med kortare avtal direkt från fastighetsägare vuxit, vilket är något helt nytt på marknaden. Mitt bästa tips är att verkligen tänka till kring hur många ni är på kontoret och hur ni förutser att det behovet förändras framåt, då det går att spara stora pengar genom att välja en effektiv lösning.`,
			},
			expert: {
				info: PAR!,
				text: 'Jag har hjälpt allt från frilansare till globala enterprise-bolag att hitta rätt kontor i Stockholm. Här kan du läsa några av mina bästa tips för hur ni kan gå tillväga.',
			},
		},
		serviced_office: {
			content: {
				heading: 'I Stockholm finns ett kontorshotell för alla',
				text: `I Stockholm har kontorshotell blivit den nya normen, ett markant skifte från bara 5-10 år sedan då begreppet i sig fortfarande var relativt okänt. Mitt namn är David Kostadinovic, det senaste året som kontorsexpert på yta.se har jag hjälpt företag komma ut på över 500 visningar och jag har personligen besökt över 100 olika anläggningar i Stockholm och tänkte dela med mig av den erfarenhet och de guldkorn jag plockat upp längs vägen.
                <br/><br/>
                <strong>Hur hittar jag ett riktigt prisvärt kontorshotell?</strong>
                <br/>
                En vanligare fråga än man tror i tider som dessa! Det finns 3 konkreta tillvägagångssätt att hålla nere kostnaden. Börja med läget, skippa Stockholm City om ni inte har affärsnytta av platsen. Bara där kan ni komma ned 50% eller mer på hyran. Kan ni tänka er utanför tull går priserna ner ännu mer. Sedan behöver ni tänka på servicen och kvalitén på kontoret. Om ni inte tar emot kunder och kan tänka er något enklare finns mycket pengar att spara genom att skippa tjänster såsom inkluderad frukost och gym, leta efter ett kontorshotell med mer kontor och mindre gemensam yta. Sist men inte minst förhandla på avtalslängden, desto längre ni kan teckna desto bättre pris kan ni få.
                <br/><br/>
                <strong>Vilka är just dina favoriter i Stockholm?</strong>
                <br/>
                I mitt andra liv är jag DJ vilket gör det svårt för mig att välja något annat än A-House Slussen som satt pricken över i:et för en attraktiv arbetsplats för kreatörer. De har verkligen lagt själ i att handplocka all möblering för att skapa en cool 60-tals feeling och inte minst vinylbaren Pet Sounds vinbar på entréplan. Ska jag tänka lite mer business gillar jag och många av mina kunder The Works, de har lyckats göra en prisvärd produkt som andas hållbarhet istället för att kännas billig.`,
			},
			expert: {
				info: DAVID!,
				text: 'Jag hjälper dagligen företag att hitta rätt och jämföra bland utbudet av kontorshotell i Stockholm. Jag ligger i topp på inbokade visningar och brinner för att hjälpa företag hitta den bästa möjliga lösningen.',
			},
		},
		coworking_space: {
			content: {
				heading: 'Coworking som svar på ett hybrid arbetssätt',
				text: `Över de senaste decennierna har kontorsanvändingen skiftat markant - från cellkontor till öppna landskap och numera den högaktuella frågan kring hybridkontoret och hur man som arbetsgivare skall tänka kring storlek på kontoret. På yta.se märker vi att osäkerheten är större än någonsin, och har sett första hand hur coworking har vuxit till att bli precis den lösningen många organisationer letat efter idag.
                <br/><br/>
                <strong>Varför passar coworking ett hybrid arbetssätt?</strong>
                <br/>
                Mitt namn är Viktor, jag är medgrundare till yta.se och har över åren hjälpt hundratals företag hitta rätt kontor. Frågan som är på tapeten för alla mina kunder nuförtiden är hur man hanterar att hyra ett kontor för hela arbetsstyrkan fastän den genomsnittliga beläggningen på kontoret ligger på 30-50%.
                <br/><br/>
                I min erfarenhet vill man uppnå kombinationen av ett attraktivt kontor som lockar in medarbetare men inte betala för överyta. Det är precis den kombon som coworking löser genom att erbjuda arbetsplatser designade i världsklass samt möjligheten att använda de gemensamma ytorna som extraytor. I praktiken innebär det att ni endast betalar för den del ni använder men aldrig riskerar att stå med ett för trångt kontor.
                <br/><br/>
                <strong>Vilka trender ser du just nu på coworkingmarknaden i Stockholm?</strong>
                <br/>
                Coworkingmarknaden i Stockholm har blivit så pass etablerad att utvecklingen går mot än mer nischade produkter. Idag finns det allt från tech-hubbar där du kan hänga med entreprenörer till lyxiga concierge-upplevelser på Östermalm eller till och med coworking spaces som från grunden designat sina produkter för större hyresgäster med mellan 20-100 anställda. Mycket av mitt jobb går ut på att förstå kundens behov och kultur och hitta rätt match på marknaden, vilket är väldigt kul!`,
			},
			expert: {
				info: VIKTOR!,
				text: 'Som VD och grundare för yta.se har det varit riktigt kul att i förarsätet följa utvecklingen av Stockholms coworkingsmarknad. Sett till många andra städer och länder har utvecklingen varit explosiv.',
			},
		},
	},
	göteborg: {
		office: {
			content: {
				heading: 'Mina bästa tips för att hitta rätt kontor i Göteborg',
				text: `Efter mina otaliga besök i Göteborg har jag snabbt insett att det är en snårig marknad vad gäller kontor. När det kommer till kontor med flexibla avtal som vi är specialister på, så är marknaden spretig och består av många mindre aktörer och fastigheter. Samtliga har sina egna erbjudanden, nischer, och uppfattningar på hur kontor ska se ut och vara designat. Med det sagt så tycker jag att det blir en extra spännande marknad då det finns ett såpass stort varierat utbud med många lokala mindre aktörer.
                <br/><br/>
                Att hitta rätt kontor kan därför kännas överväldigande och upplevas som en stor uppgift. Därför är mina bästa tips för att hitta rätt kontor i Göteborg att försöka gå på så många visningar som möjligt för att verkligen få en bra bild och uppfattning av utbudet och de olika alternativ som finns. Det finns många som kan hjälpa till med detta, men för att slå ett slag för mig och våra tjänster så är det helt kostnadsfritt att ta hjälp av mig. Jag kommer arbeta tillsammans med er och agera som en spindel i nätet där jag sållar bland alla vakanta kontor som finns, inklusive de som inte är utannonserade ännu eller ligger publikt synliga, genom mig får ni alltså tillgång till hela spektrat av lediga kontor i Göteborg - och jag följer med i hela processen från kravspecifikation till signerat avtal för att säkerställa att ni får bästa möjliga lösning.`,
			},
			expert: {
				info: DAVID!,
				text: 'Göteborgs kontorsmarknads är otroligt spännande och det finns mycket att välja bland. Efter regelbundna besök och många företag hjälpta; här kommer mina bästa tips för hur du ska navigera kontorsmarknaden i Göteborg.',
			},
		},
		serviced_office: {
			content: {
				heading: 'Varför ska man hyra kontorshotell i Göteborg?',
				text: `Jag har arbetat med förmedling av kontorshotell i många år nu och hjälpt allt från frilansare och start-ups till globala entreprise-bolag med kontorshotellslösningar i hela Sverige, Norden och framförallt Göteborg. Om det är något jag har lärt mig efter mina år av erfarenhet är det att jag verkligen älskar Göteborgs kontorshotellsmarknad. Jag tycker det är ett perfekt exempel på hur en bra marknad ska se ut - fullproppat av lokala aktörer som brinner för deras verksamheter, är tillmötesgående mot sina hyresgäster och säkerställer en sällan skådad servicenivå.
                <br/><br/>
                Även om det givetvis finns både globala och nationella aktörer i Göteborg är det i regel de lokala kontorshotellen som visar framfötterna i dagens tuffa kontorshotellsmarknad. Under 2024 var Göteborg den marknad som växte mest i termer av kvadratmeter kontorshotell - och det är till största del tack vare lokala aktörer som bara har kontorshotell i Göteborg.
                <br/><br/>
                <strong>Mina personliga favoriter bland Göteborgs kontorshotell</strong>
                <br/>
                En riktig favorit för mig är Dockside kontorshotell på Lindholmen - ett typexempel på en mindre lokal aktör som har örat mot rälsen, lyssnar på sina hyresgäster och marknaden, och alltid har ett skräddarsytt erbjudande anpassat till kunden. En annan av mina favoriter är Entreprenörsgatan med deras sex olika kontorsotell, den jag tycker sticker ut mest här är Hasselbladshuset med sin anrika historia av uppfinningsrikedom och det är extra kul att innovation, tack vare Entreprenörsgatan, tillåts fortsätta äga rum i lokalerna.`,
			},
			expert: {
				info: PAR!,
				text: 'Jag är försäljningschef hos yta.se och innan dess ansvarig för förmedling av kontorshotell i Göteborg. Det är en riktigt spännande marknad med många duktiga lokala aktörer. Om ni står inför en flytt bör ni verkligen överväga kontorshotell!',
			},
		},
		coworking_space: {
			content: {
				heading: 'Coworking i Göteborg - en växande markand',
				text: `I samband med att vi grundade yta.se och bestämda oss för att rikta in oss på att hjälpa företag att hitta rätt coworking space visste vi att Göteborg hade en snabbt växande marknad och redan då låg i framkant med sina coworking spaces. Det är många år sedan nu vi grundade yta.se och vår erfarenhet inom allt som rör coworking har givetvis växt mer eller mindre exponentiellt - liksom marknaden har gjort.
                <br/><br/>
                <strong>Vad utmärker Göteborgs coworkingmarknad?</strong>
                <br/>
                I min roll som grundare och förmedlare hjälper jag många globala företag genom vårt moderbolag The Instant Group. Göteborg är en väldigt viktig metropol för många av dessa bolag. I och med att Göteborg är hem för så pass många andra storbolag som till exempel. Volvo, SKF och AstraZeneca för att nämna några är det viktigt för många andra att ha en fysisk närhet och koppling till dem. Många vill även komma åt talang från Chalmers eller ha hamntillgång för smidigare transporter. För många av dessa företag behöver de inga stora kontor och har hundratals anställda, med vissa undantag förstås, utan det handlar ofta om att ha man vill någonstans att landa och behöver en adress och tillgång till mötesrum och lite annan service, det viktigaste är oftast läget för dessa företag och att det ska vara representativt -  något som Göteborgs coworking spaces tillgodoser alldeles utmärkt och det finns många alternativ att välja på.`,
			},
			expert: {
				info: TIM!,
				text: 'Jag är grundare och COO för yta.se samt ytterst ansvarig för våra samarbeten med coworking spaces. Att lansera vår tjänst för coworking i Göteborg var riktigt kul, att lära känna marknaden och alla lokala aktörer.',
			},
		},
	},
	malmö: {
		office: {
			content: {
				heading: 'Mina tankar om kontorsmarknaden i Malmö',
				text: `I min roll som VD för yta.se har jag varit med och expanderat vår tjänst till samtliga städer. Jag tycker att Malmö är en otroligt spännande kontorsmarknad med sin närhet till Danmark och andra större städer i södra Sverige, det gör det till en livlig och internationellt fokuserad marknad där gränserna mellan städer och länder i princip har suddats ut. Här finns många Danska aktörer, liksom aktörer med starkt fokus på hela södra Sverige och även lokala aktörer som endast är verksamma i Malmö.
                <br/><br/>
                Vidare har Malmö en stark start-up scen och är även hem för många större svenska företag som AAK, Boozt och Bygg Hemma Group. Detta lockar givetvis många andra till Malmö och med närheten till Lunds Universitet finns ett starkt tillflöde av bra kompetens för företag vilket skapar goda förutsättningar för tillväxt.
                <br/><br/>
                Kontorsmässigt stöttas detta upp på ett bra sätt med många fina och diversifierade alternativ. Som jag nämnde tidigare är gränserna flexibla här med många aktörer och fastighetsägare som ger dig som hyresgäst tillgång till allt från Danmark till Helsingborg, Lund, Göteborg och vidare ut i landet. En stor fördel med flexibla kontor är att du ofta får tillgång till ett större nätverk av kontorslokaler eller åtminstone möjlighet att boka mötesrum eller kontor för en dag eller två på andra orter.`,
			},
			expert: {
				info: VIKTOR!,
				text: 'Jag har förmedlat många kontor i Malmö som är en riktigt rolig och livlig marknad där det finns mycket att välja på. Med närheten till Danmark och vidare ut i Europa är det förståeligt att många vill hyra kontor i Malmö.',
			},
		},
		serviced_office: {
			content: {
				heading: 'Om kontorshotellsmarknaden i Malmö',
				text: `För att försöka sammanfatta mina tankar något gällande marknaden i stort skulle jag säga att det är en delvis internationellt och delvis nationellt präglad marknad, med några aktörer som tagit sig över sundet från Danmark och många som finns runtom i Sveriges största städer som t.ex. United Spaces, Quick Office och Embassy House. Det finns även globala aktörer som Regus och Spaces som finns i många länder, liksom aktörer som (änsålänge) uteslutande fokuserar på södra Sverige som Malmö, Lund och Helsingborg - till exempel High Court, Workopolis och Genetor.
                <br/><br/>
                <strong>Hyresnivåerna för kontorshotell i Malmö</strong> är attraktiva och bra givet utbudet om du frågar mig. Om man jämför med andra städer i Sverige kan man hitta riktiga kap bland kontorshotellen här i Malmö som jag har svårt att se hade gått någon annanstans i Sverige. Att det finns såpass många med vacker och härlig havsutsikt och riktigt god service gör det till en extra attraktiv marknad.
                <br/><br/>
                <strong>Mitt personliga favorit kontorshotell i Malmö</strong>
                <br/>
                Det är en riktigt svår fråga då det finns många jag verkligen tycker om, men om jag bara får välja ett så måste valet nog falla på Workopolis. Delvis för att det är en helt lokal aktör vilket jag gillar och vill främja generellt, de ligger bra till prismässigt och har några av mest prisvärda lösningarna i Malmö - samtidigt som de ligger centralt i Västra Hamnen med en magisk utsikt och en riktigt härlig terrass. Det finns även större lösningar här för upp till 90 fasta arbetsplatser vilket inte är helt vanligt annars.`,
			},
			expert: {
				info: PAR!,
				text: 'Idag är jag försäljningschef på yta.se och var tidigare ansvarig för kontorshotell i Malmö. Detta medförde många resor till staden och att jag skapade mig en bra bild av kontorshotellsmarknaden i Malmö.',
			},
		},
		coworking_space: {
			content: {
				heading: 'Om Malmös coworking marknad',
				text: `Generellt om marknaden skulle jag säga att det är en diversifierad coworkingmarknad med många aktörer sett till antalet coworking spaces, och det har även skett mycket här senaste åren där aktörer expanderat och konsoliderat. Min känsla är att Malmö har blivit lite som en ingång till Sverige, både för företag som hyr in sig på coworking spaces här, men även för operatörerna som driver coworking då jag märker att allt fler rör sig ut i landet till andra städer efter att initialt ha grundats och lanserat i Malmö. Idag är det många som initialt bara fanns i Malmö men som nu är ett grepp som södra Sverige i stort, då de ofta öppnar i Helsingborg, Lund och Kalmar till exempel.
                <br/><br/>
                <strong>Mitt bästa tips för att hyra rätt coworking space i Malmö</strong>
                <br/>
                Jag skulle säga att ett av mina bästa tips är att utnyttja en av coworking spaces grundpelare: flexibiliteten i avtal. Det är kanske lite svårare om ni är lite fler (säg 15+) anställda som ska sitta på samma, men annars är det superenkelt att testa sig runt på olika ställen. Självklart ska man gå på många visningar och känna att man väljer rätt, men faktum är att du skriver ett löpande avtal på 1-3 månader, och om du skulle känna att det inte var rätt efter ett par veckor är det enkelt att testa något annat. Således är mitt bästa tips är att spela ner det hela lite, det är inte som med traditionella kontor förr i tiden då du var tvungen att binda upp dig i flera år, utan att byta kontor då och då är både kul och vanligt bland de som hyr coworking.`,
			},
			expert: {
				info: DAVID!,
				text: 'Jag är kontorsexpert hos yta.se och fokuserar framförallt på företag och entreprenörer som söker arbetsplatser på coworking spaces i Malmö. Här kommer mina bästa tips för hur du kan hitta rätt coworking space.',
			},
		},
	},
	'stockholm/östermalm': {
		office: {
			content: {
				heading: 'Att hitta det perfekta kontoret på Östermalm',
				text: `Östermalm är ett populärt område där vakanserna ofta är låga, vilket innebär att det kräver att du som företagare är på hugget för att hitta rätt. Av de kunder jag hjälpt hitta drömkontoret på Östermalm är min erfarenhet att de ofta ställer höga krav på kontoret utöver läget, varför jag delar med mig av mina bästa tips på att hitta rätt nedan.
                <br/><br/>
                <strong>Ta hjälp av en kontorsexpert</strong>
                <br/>
                Allt utbud finns inte alltid publikt när det kommer till den exklusiva stadsdelen Östermalm. Utbudet rör på sig snabbt och det går ofta att hitta guldkorn som inte hunnit annonseras innan de ryker. Genom att boka ett förutsättningslöst möte med mig eller en av mina kollegor går vi igenom era behov och scoutar snabbt fram de alternativ som passar er bäst, så att ni inte riskerar att missa något.
                <br/><br/>
                <strong>Boka en visningsrunda på Östermalm</strong>
                <br/>
                Det bästa sättet att bilda dig en uppfattning kring utbudet och tillskansa dig ett grundligt beslutsunderlag är att komma ut på en visningsrunda. Då Östermalm är relativt litet sett till ytan går det enkelt att hinna med 3-5st visningar och träffa alla stadsdelens operatörer på en förmiddag eller eftermiddag.Ta hjälp av våra kontorsexperter så ordnar vi det åt dig på en tid som passar.`,
			},
			expert: {
				info: PAR!,
				text: 'Jag heter Pär Lövgren och har förmedlat fler kontor på Östermalm än någon annanstans. I och med att området är så populärt är det inte alltid helt lätt att hitta precis rätt kontor för era behov. Läs mina bästa tips här!',
			},
		},
		serviced_office: {
			content: {
				heading: 'Östermalms kontorshotellsmarknad',
				text: `Om det finns en stadsdel i Stockholm som är så pass eftertraktad bland kontorshotellshyresgäster att de allt som oftast bara söker inom just det området, då är det Östermalm. Varför är det så? Vår Östermalmsexpert Tim förklarar kontorshotellsmarknaden i området närmare.
                <br/><br/>
                <strong>Lyxiga kontor på Östermalm</strong>
                <br/>
                Precis som med hotell så återfinns några av Sveriges absolut mest lyxiga kontorshotellsupplevelser på Östermalm. Höga grundhyror på fastigheterna, påkostad arkitektur samt en krävande kundgrupp lägger alla grunden till en marknad där kontorshotellen konkurrerar kring den mest representativa upplevelsen. Oavsett om det är concierge-service med 3-rätters lunch eller en egen balkong som väller ut över Strandvägen så är det enkelt att hitta något fint om du blundar för prislappen. Min favorit? No18 på Blasieholmen tack vare den 300kvm stora takterrassen som nog är Stockholms bästa.
                <br/><br/>
                <strong>Östermalm - En stadsdel med två kontorshotellsmarknader</strong>
                <br/>
                Med Sveriges högsta hyror per kvadratmeter är Östermalms kontorshotellsmarknad trots det väldigt spridd. Topphyrorna är koncentrerade i ett väldefinierat område kring Kungsgatan, Stureplan och Nybroviken. Går du utanför dessa områden upp mot Spårvagnshallarna, Karlaplan eller rent av till en bakgata, går det ofta att komma ner mer än 50% i hyra. Tänk noga kring vad som verkligen är viktigt för er när det kommer till läget så finns det mycket pengar att spara.
                <br/><br/>
                <strong>Vad kostar kontorshotell på Östermalm?</strong>
                <br/>
                Just på Östermalm är det viktigare än någonsin att tänka kring er totala månadshyra istället för hyran per kvadratmeter. Anledningen är att inkluderandet av high end tjänster och mycket loungeytor, som det ofta är på Östermalm, kan påverka priset kraftigt. Enligt yta.se statistik kostar det i snitt 6 700 kr/person/månad för kontorshotell på Östermalm, med lägsta hyror kring 3-4000kr och toppnoteringar kring 12-15000 kr/person/månad.`,
			},
			expert: {
				info: TIM!,
				text: 'Jag heter Tim och har förmedlat kontorshotell på Östermalm sedan vi grundade yta.se. Det är en unik marknad i det avseendet att många som söker här, inte kan tänka sig att hyra kontorshotell någon annanstans.',
			},
		},
		coworking_space: {
			content: {
				heading:
					'Coworking på Östermalm - ett alternativ till traditionella kontor',
				text: `Coworking har blivit nutidens svar på ett flexibelt och kreativt arbetssätt för företagare och entreprenörer som vill jobba på Östermalm. På grund av de höga hyrorna och stora kontorsytorna har det länge varit svårt att få in en fot i stadsdelen, men coworking har gjort området lättillgängligt för alla bolag genom flexibla medlemskap och ett dynamiskt utbud.
                <br/><br/>
                <strong>Några utav mina favoriter</strong>
                <br/>
                De olika operatörerna bjuder alla på olika intryck och inte minst sammanhang. För företag inom finans är Convendum och KG10 ofta populära, med sina representativa miljöer och professionella tjänster. Är event och nätverkande viktigt för dig? Kolla på Helio GT30, där det händer något nytt varje dag. Vill man däremot verkligen få känna av Östermalm rekommenderar jag coworking spaces kring Nybroviken, vad säger inte Stockholm mer än en promenad längs med Strandvägen på lunchen?
                <br/><br/>
                <strong>Coworking eller eget kontor på Östermalm</strong>
                <br/>
                Coworking har många fördelar för den som vill jobba på Östermalm - korta avtalstider och enkelhet är det som brukar uppskattas mest. För andra är det möjligheten att bjuda in kunder och partners till en typ av kontorsmiljö som hade varit svår att skapa själv. Alternativet - att hyra ett helt eget kontor, tillåter dig att istället skapa en upplevelse som centrerar mer kring ert eget företag.`,
			},
			expert: {
				info: DAVID!,
				text: 'Jag heter David och är kontorsexpert på yta.se. Sin relativt lilla yta till trots, så finns det många coworking spaces på Östermalm som verkligen tilltalar målgruppen som oftast söker här, men även mer prisvärda och avskalade alternativ.',
			},
		},
	},
	'stockholm/södermalm': {
		office: {
			content: {
				heading: 'Vad finns det för lediga kontor på Södermalm i dagsläget?',
				text: `De senaste åren har präglats av en pandemi följt av lågkonjunktur vilket såklart skapat en missgynnsam situation för fastighetsägare överallt, och inte minst på Södermalm. På Södermalm har vakansgraden nått rekordhöga nivåer och fastighetsägare har behövt tänka om sina erbjudanden för att möta dagens nya efterfrågan på kontor.
                <br/><br/>
                Efter några år av tuffare tider med pandemi och lågkonjuktur har fastighetsägare och uthyrare blivit mer flexibla i sitt tänk och innovativa i sina erbjudanden. Idag erbjuder i princip alla fastighetsägare en bredare portfölj av avtalsval där du till exempel kan hyra ett eget traditionellt kontor, men också lägga till service, skriva kortare löpande avtal eller både och. Många fastighetsägare har även antingen köpt upp befintliga kontorshotell, eller lanserat sina egna flexibla koncept.
                <br/><br/>
                Jag upplever att detta i praktiken inneburit att allt fler fastighetsägare blivit mer flexibla i sitt tänk och sina erbjudanden. Idag erbjuder i princip alla fastighetsägare på Södermalm kontor med flexibla avtal och/eller service, då det är vad många företag efterfrågar. Eftersom så många idag erbjuder hybridarbete har kontoret blivit mycket mer än bara en plats att arbeta från, det har blivit mer av en mötesplats dit man åker för att träffa sina kollegor och få en social injektion, snarare än ett utrymme som måste tillgodoses av alla företag som alla anställda måste pendla till och från varje dag. Min åsikt är att företag i allt högre utsträckning försöker få sina anställda att komma till kontoret frivilligt och att allt som kan underlätta för det uppskattas. Till exempel service som frukost, gym, parkering, centrala lägen och liknande. Detta återfinns inte minst på Södermalm som är en minst sagt attraktiv stadsdel att hyra kontor i, och det investeras nu tungt i att utveckla fastigheter och kontor som möter just dessa krav. Jag tänker till exempel på Atrium Ljungberg som öppnade A-House i Slussen, AMF och Metropole som har stora planer för området vid södra station och Vasakronans exklusiva Arena i Skrapan för att nämna några.`,
			},
			expert: {
				info: PAR!,
				text: 'Jag är ytterst ansvarig för kontorsförmedlingen hos oss på yta.se och har varit med på marknaden i många år nu. Det har varit en minst sagt turbulent tid inte minst på Södermalms kontorsmarknad. Men det finns tydliga ljusglimtar!',
			},
		},
		serviced_office: {
			content: {
				heading:
					'Kontorshotell på Södermalm - en levande marknad i ständig förändring',
				text: `Jag är VD och grundare för yta.se och håller löpande dragningar om kontorshotellsmarknaden för allt från fastighetsbolag till diverse paneler och företag som är nyfikna på kontorshotell. Södermalm kommer ofta på tal och jag använder det ofta som exempel då marknaden har genomgått relativt stora förändringar de senaste åren.
                <br/><br/>
                Några av de mest notervärda förändringarna var under 2024 då The Park annonserade att de skulle stänga sitt flaggskepp i Stockholmsverken, vid tidpunkten Södermalms största kontorshotell sett till bruttoyta. Vi spekulerade mycket i vad som skulle hända med fastigheten och förde även dialoger med AMF gällande fastighetens framtid. Glädjande nog tog aktören Metropole tillvara på möjligheten och fastigheten, och är nu i full fart att omvandla inte bara Stockholmsverken, utan hela området till en kultur-destination. I sann söder-anda hittade många spelbolag och andra kreativa branscher hit snabbt, och det finns stora planer på området för framtiden.
                <br/><br/>
                Även Vasakronan har öppnat sitt exklusiva kontorshotells-koncept Arena i Skrapan och A House som till största del ägs av Atrium Ljungberg öppnade i Katarinahuset i Slussen. Trots att de två sistnämnda är två av Södermalms dyraste kontorshotell har det gått väldigt bra för dem och blivit i princip fullt uthyrda på väldigt kort tid. Det är enkelt att förstå om man besöker någon av anläggningarna, A House med sin otroliga havsutsikt mot Djurgården och Stockholms inlopp eller Arenas påkostade inredning och genomtänkta arbetsplatsstrategi.
                <br/><br/>
                För mig skapar dessa tillskott i kombination med flera av The Works kontorshotell en komplett kontorshotellsmarknad på Södermalm. Där du kan börja med ett av Stockholms billigaste kontor och arbeta dig upp till ett av de dyraste.`,
			},
			expert: {
				info: VIKTOR!,
				text: 'Som VD och grundare för yta.se har kontorshotell på Södermalm alltid varit lite av en favorit för mig. Inte minst för att jag bodde här när vi startade yta, men också för att det är en så rolig kontorshotellsmarknad med spännande alternativ.',
			},
		},
		coworking_space: {
			content: {
				heading: 'Södermalms coworkingmarknad',
				text: `Södermalm är enligt mig en superrolig marknad att arbeta på då det är så många häftiga företag och personer som bor och arbetar här. Det känns också som att Södermalm växer i och med stadsdelar som till exempel Slakthusområdet och Hammarby Sjöstad där det kommit mycket nytt utbud som konkurrerar med Södermalm då du ofta kan komma ner lite i hyra och få en bättre eller lika bra standard.
                <br/><br/>
                <strong>Mina personliga favoriter på Södermalm</strong> är A House Slussen och Metropole Södra. A House för att de har en otroligt snygg produkt, här får man verkligen energi från det generösa ljusinsläppet och det är en riktigt härlig vibe här. Det ligger även riktigt bra till geografiskt sett precis intill Slussens tunnelbana. Metropole Södra gillar jag mycket tack vare deras tydliga nisch, som förvisso inte är unik för metropole Södra utan alla Metropole, men eftersom det här coworking spacet är så stort har de kunnat ta ut svängarna ordentligt och installerat allt från VR- och tv spelsrum, restaurang, inspelningsstudios och många fler saker som deras typiska klientel önskar. Här är det också en grym vibe och härliga företag som hyr in sig, samt att de som driver Metropole är ett riktigt härligt gäng som man behöver anstränga sig för att inte gilla.`,
			},
			expert: {
				info: DAVID!,
				text: 'Jag är kontorexpert hos yta.se och fokuserar främst på vår förmedling av coworking spaces i Stockholm och extra mycket på Södermalm. Nedan följer lite tips som kan vara värda att ha med sig om ni letar coworking på Södermalm.',
			},
		},
	},
}

const AREA_NAMES = {
	stockholm: 'Stockholm',
	göteborg: 'Göteborg',
	malmö: 'Malmö',
	'stockholm/östermalm': 'Östermalm',
	'stockholm/södermalm': 'Södermalm',
}

type BannerProps = {
	content: { heading: string; text: string }
	expert: {
		name: string
		info: SalesPerson
		text: string
	}
} & ChakraProps

const SeoBannerDesktop: FC<
	BannerProps & { areaName: string; officeType: string }
> = ({ content, expert, areaName, officeType, ...rest }) => {
	const { t } = useTranslation()
	return (
		<Flex
			display={{ base: 'none', md: 'flex' }}
			gap={5}
			mt={{ base: 2, md: 10 }}
			py={6}
			borderTop="1px solid"
			borderTopColor="gray.100"
		>
			<SeoBannerWrapper variant="vertical" w="40%" {...rest}>
				<SeoBannerMainSection>
					<SeoBannerAvatar>
						<Image src={expert.info.img} borderRadius="full" width="100px" />
					</SeoBannerAvatar>
					<SeoBannerImageText>
						{t('Tips från experten')} <br /> {expert.name}
					</SeoBannerImageText>
					<SeoBannerMainText>{t(expert.text)}</SeoBannerMainText>
					<SeoBannerButton as={Link} to={useTo()} textAlign="center">
						{t('Bli matchad')}
					</SeoBannerButton>
				</SeoBannerMainSection>
				<SeoBannerFooter>
					<Text my={2} fontSize="xs">
						{t('Go-to för {{officeType}} i {{area}}', {
							area: areaName,
							officeType: officeType,
						})}
					</Text>
					<Flex
						my={1}
						justifyContent="space-between"
						alignItems="baseline"
						flexWrap="wrap"
						gap={2}
					>
						<Image h={{ base: 5, md: 8 }} src={HumbleGroupImg} />
						<Image h={{ base: 5, md: 7 }} src={EssveeImg} />
						<Image h={{ base: 5, md: 7 }} src={LissieImg} />
						<Image h={{ base: 4, md: 6 }} src={JuniImg} />
					</Flex>
				</SeoBannerFooter>
			</SeoBannerWrapper>
			<Box w="60%">
				<Text fontSize="2xl" fontWeight="semibold" mb={3}>
					<Trans i18nKey={content.heading}>{content.heading}</Trans>
				</Text>
				<Text color="grey.500" mb={3}>
					<Trans i18nKey={content.text}>{content.text}</Trans>
				</Text>
			</Box>
		</Flex>
	)
}

const SeoBannerMobile: FC<BannerProps> = ({ content, expert, ...rest }) => {
	const { t } = useTranslation()
	const { isOpen, onToggle } = useDisclosure()
	return (
		<Flex
			display={{ base: 'flex', md: 'none' }}
			gap={2}
			pt={3}
			direction="column"
			borderTop="1px solid"
			borderTopColor="gray.100"
		>
			<SeoBannerWrapper variant="horizontal" {...rest}>
				<SeoBannerMainSection>
					<SeoBannerAvatar>
						<Image src={expert.info.img} borderRadius="full" width="90px" />
					</SeoBannerAvatar>
					<SeoBannerImageText>
						{t('Tips från experten')} <br /> {expert.name}
					</SeoBannerImageText>
					<SeoBannerMainText>{t(expert.text)}</SeoBannerMainText>
					<SeoBannerButton as={Link} to={useTo()} textAlign="center">
						{t('Bli matchad')}
					</SeoBannerButton>
				</SeoBannerMainSection>
			</SeoBannerWrapper>
			<Box
				mb={isOpen ? '' : '1rem'}
				maxHeight={isOpen ? '' : '320px'}
				position="relative"
				borderBottom={isOpen ? '' : '1px solid rgb(255, 255, 255)'}
				overflow={isOpen ? '' : 'hidden'}
				transition={isOpen ? '' : 'max-height 0.3s ease-in-out 0s'}
			>
				<Text fontSize="xl" mb={3}>
					<Trans i18nKey={content.heading}>{content.heading}</Trans>
				</Text>
				<Text color="grey.500" mb={3}>
					<Trans i18nKey={content.text}>{content.text}</Trans>
				</Text>
			</Box>
			<Button
				variant="link"
				color="blue.500"
				textDecoration="none"
				mb={5}
				size="sm"
				width={{
					base: '100%',
					md: 'min-content',
				}}
				onClick={() => {
					onToggle()
				}}
			>
				{isOpen ? t('Visa mindre') : t('Läs mer')}
			</Button>
		</Flex>
	)
}

export const SeoBanner: FC<
	{ pageType: string | undefined; areaUrl: string | undefined } & ChakraProps
> = ({ pageType = '', areaUrl, ...rest }) => {
	const { t } = useTranslation()
	const isEnhancedArea = getIsEnhancedSERPUrl(areaUrl)
	const officeType = getEnhancedSERPOfficeType(pageType, t)

	if (!isEnhancedArea || !officeType) {
		return null
	}
	const officeTranslation = getOfficeTypeTranslation(officeType, t)
	const content = BANNER_CONTENT[areaUrl][officeType].content
	const expert = BANNER_CONTENT[areaUrl][officeType].expert
	return (
		<>
			<SeoBannerDesktop
				expert={{
					name: getTeamMemberName(expert.info) || '',
					info: expert.info,
					text: expert.text,
				}}
				content={content}
				areaName={AREA_NAMES[areaUrl]}
				officeType={officeTranslation}
				{...rest}
			/>
			<SeoBannerMobile
				expert={{
					name: getTeamMemberName(expert.info) || '',
					info: expert.info,
					text: expert.text,
				}}
				content={content}
				{...rest}
			/>
		</>
	)
}
